import { SelectLocationSources } from "@/analytics/generic-events-properties";
import { useStoreCameraWithCustomControls } from "@/components/common/view-runtime-context";
import { CameraAnimation } from "@/components/r3f/animations/camera-animation";
import { useDefaultCamera } from "@/components/r3f/cameras/active-camera-context";
import { useReduceDprRange } from "@/components/r3f/canvas/adaptive-resolution-canvas";
import { ZoomLensClipProvider } from "@/components/r3f/renderers/measurements/zoom-lens-clip";
import { SuspenseLoadNotifier } from "@/components/r3f/utils/suspense-load-notifier";
import { useValidateCacheSize } from "@/object-cache";
import {
  selectFlyWalkSceneFilter,
  setFlyWalkSceneFilter,
} from "@/store/modes/flywalk-mode-slice";
import { setActiveElement } from "@/store/selections-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { SceneFilter } from "@/types/scene-filter";
import { IS_DEV } from "@/utils/env-constants";
import { selectIElement, View } from "@faro-lotv/app-component-toolbox";
import {
  IElementType,
  type IElementGenericImgSheet,
} from "@faro-lotv/ielement-types";
import { ForwardMovement } from "@faro-lotv/lotv";
import { JSX, RefObject, Suspense, useCallback, useRef, useState } from "react";
import { ModeSceneProps, SceneFilterLookAtInitialState } from "../mode";
import { useCurrentArea, useCurrentScene } from "../mode-data-context";
import { selectBestModelCameraFor360 } from "../walk-mode/animations/pano-to-model";
import { selectWaypointTargetSceneFilter } from "../walk-mode/walk-scene-selectors";
import { InitialAnimationInfo } from "./data-dispatch/flywalk-animation";
import {
  DataDispatchResult,
  FlyWalkDataDispatch,
  INITIAL_DATA_DISPATCH_RESULT,
} from "./data-dispatch/flywalk-data-dispatch";
import { useDowncastObjects } from "./data-dispatch/flywalk-renderables";
import {
  FlyWalkAnimationData,
  INITIAL_FLYWALK_ANIMATION_DATA,
} from "./data-dispatch/use-flywalk-animation";
import type { MoveCameraToCbk } from "./data-dispatch/use-move-camera-to";
import { FlyWalkControls } from "./flywalk-controls";
import { FlyWalkMinimap } from "./flywalk-minimap";
import { FlyWalkWaypoint, useTrackSelectLocation } from "./flywalk-types";
import { FlyWalkRenderers } from "./renderers/flywalk-renderers";
import { FlyWalkRenderingPipeline } from "./rendering-pipeline/flywalk-rendering-pipeline";
import { FlyWalkTools } from "./tools/flywalk-tools";
import { ToolsAcquaintanceContextProvider } from "./tools/tools-acquaintance-context";
import { useFlyWalkModeToolVisibility } from "./tools/use-fly-walk-tools-visibility";
import {
  useOrthoCameraForImages,
  usePerspectiveCameraForNonImages,
} from "./use-orthocamera-for-images";

/** @returns The main Scene for the FlyWalk Mode */
export function FlyWalkScene({
  initialState,
}: ModeSceneProps<SceneFilterLookAtInitialState>): JSX.Element {
  const lookAtElement = useAppSelector(
    selectIElement(initialState?.lookAtId ?? ""),
  );
  const canSkipInitialTransition = !!(initialState && initialState.camera);

  const initialInfo = useRef<InitialAnimationInfo | null>({
    lookAtElement,
    canSkipInitialTransition,
  });

  return (
    <ToolsAcquaintanceContextProvider>
      <FlyWalkSceneImpl initialAnimationInfo={initialInfo} />
    </ToolsAcquaintanceContextProvider>
  );
}

type FlyWalkSceneImplProps = {
  /** Information about the initial animation when entering flywalk mode */
  initialAnimationInfo: RefObject<InitialAnimationInfo | null>;
};

function FlyWalkSceneImpl({
  initialAnimationInfo,
}: FlyWalkSceneImplProps): JSX.Element {
  const activeCamera = useDefaultCamera();
  useStoreCameraWithCustomControls();

  const flyWalkSceneFilter = useAppSelector(selectFlyWalkSceneFilter);

  const currentScene = useCurrentScene();
  const currentArea = useCurrentArea();

  const [dataDispatchResult, setDataDispatchResult] =
    useState<DataDispatchResult>(INITIAL_DATA_DISPATCH_RESULT);

  const [animationData, setAnimationData] = useState<FlyWalkAnimationData>(
    INITIAL_FLYWALK_ANIMATION_DATA,
  );

  // 'dataDispatchResult' contains the project data that should be dispatched to this scene for rendering,
  // according to the user selection and the scene filter
  const {
    main,
    mainObject,
    overlayObject,
    sheetObjects,
    annotations,
    measurements,
    imageType,
  } = dataDispatchResult;

  usePerspectiveCameraForNonImages(main);

  // 'animationData' contains the current animation state and the previous main object for rendering.
  // Notably, the 'currentSceneFilter' below is different from the 'flyWalkSceneFilter' above.
  // The latter refers to what the user has selected in the UI. The former refers to what the
  // rendering pipeline is ready to take as input (as opposed to an animation)
  const {
    flyWalkAnimation,
    currentSceneFilter,
    previousMain,
    previousMainObject,
    endAnimation,
    moveCameraTo,
  } = animationData;

  useOrthoCameraForImages(
    main,
    flyWalkAnimation,
    previousMain === null &&
      !!initialAnimationInfo.current?.canSkipInitialTransition,
  );

  // Retrieves the current renderable objects with their correct types.
  const renderables = useDowncastObjects(
    mainObject,
    overlayObject,
    previousMainObject,
    currentSceneFilter,
    currentScene.activeSheets,
  );

  const onWaypointClicked = useOnWaypointClicked(
    currentSceneFilter,
    currentScene.activeSheetFor2DNavigation,
    moveCameraTo,
  );

  const { onPanoClicked, onPanoClickedInMinimap } = useOnPanoClicked(
    currentSceneFilter,
    onWaypointClicked,
  );

  useReduceDprRange();

  useFlyWalkModeToolVisibility(main ?? undefined, currentSceneFilter);

  return (
    <>
      <Suspense
        fallback={<SuspenseLoadNotifier withSnapshotRenderer={false} />}
      >
        <FlyWalkDataDispatch
          flyWalkSceneFilter={flyWalkSceneFilter}
          currentScene={currentScene}
          currentArea={currentArea}
          initialAnimationInfo={initialAnimationInfo}
          setDataDispatchResult={setDataDispatchResult}
          setAnimationData={setAnimationData}
        />
      </Suspense>
      <View camera={activeCamera}>
        <ZoomLensClipProvider>
          <FlyWalkRenderers
            renderables={renderables}
            sheets={sheetObjects}
            panos={currentScene.panos}
            paths={currentScene.paths}
            sheetForElevation={currentScene.activeSheetFor2DNavigation}
            annotations={annotations}
            measurements={measurements}
            animation={flyWalkAnimation?.renderingAnimation ?? null}
            sceneFilter={currentSceneFilter}
            imageType={imageType}
            isOverview={false}
            onPanoClicked={onPanoClicked}
            onWalkToPosition={flyWalkAnimation ? undefined : moveCameraTo}
          />
          <FlyWalkControls
            imageType={imageType}
            animation={flyWalkAnimation}
            target={undefined}
            forwardMovement={ForwardMovement.Walk}
            clickableByControls={[]}
          />
          <FlyWalkRenderingPipeline
            pointCloud={renderables.pointCloud}
            cadModel={renderables.cadModel}
            sceneFilter={currentSceneFilter}
            animation={flyWalkAnimation?.renderingAnimation ?? null}
            endAnimation={endAnimation}
          />
          <FlyWalkTools
            sceneFilter={currentSceneFilter}
            renderables={renderables}
            isAnimating={!!flyWalkAnimation}
            imageType={imageType}
            paths={currentScene.paths}
          />
        </ZoomLensClipProvider>
      </View>
      {flyWalkAnimation?.cameraAnimation && (
        <CameraAnimation
          {...flyWalkAnimation.cameraAnimation}
          onAnimationFinished={endAnimation}
        />
      )}
      {flyWalkAnimation?.targetCameraAnimation && (
        <CameraAnimation
          {...flyWalkAnimation.targetCameraAnimation}
          syncControlsWithCameraAtEnd={false}
        />
      )}
      <FlyWalkMinimap
        activeCamera={activeCamera}
        imageType={imageType}
        isPanoWithValidPose={annotations.isPanoWithValidPose}
        isPanoAligned={annotations.isPanoAligned}
        sceneFilter={currentSceneFilter}
        targetCameraPosition={
          flyWalkAnimation?.cameraAnimation?.position ?? undefined
        }
        moveCameraTo={flyWalkAnimation ? undefined : moveCameraTo}
        onWaypointClicked={onPanoClickedInMinimap}
      />
      {IS_DEV && <ObjectCacheValidator />}
    </>
  );
}

export type OnWaypointClicked = (pano: FlyWalkWaypoint) => void;

/**
 *
 * @param currentSceneFilter the current scene filter
 * @param onWaypointClicked callback issued when a new waypoint must become the active element
 * @param moveCameraTo callback issued when the camera must be moved to another position
 * @param activeSheet reference sheet to compute the pano positions
 * @returns A callback to handle the event of the user clicking a waypoint in walk mode
 */
export function useOnPanoClicked(
  currentSceneFilter: SceneFilter,
  onWaypointClicked: (waypoint: FlyWalkWaypoint) => void,
): {
  onPanoClicked: OnWaypointClicked;
  onPanoClickedInMinimap: OnWaypointClicked;
} {
  const trackSelectLocation = useTrackSelectLocation();

  const panoClickedCallback = useCallback(
    (pano: FlyWalkWaypoint, viaMinimap: boolean) => {
      if (currentSceneFilter === SceneFilter.Pano) {
        trackSelectLocation(
          viaMinimap
            ? SelectLocationSources.minimap
            : SelectLocationSources.panoViewPanoClick,
        );
      } else {
        trackSelectLocation(
          viaMinimap
            ? SelectLocationSources.minimap
            : SelectLocationSources.panoViewWaypointClick,
        );
      }
      onWaypointClicked(pano);
    },
    [onWaypointClicked, currentSceneFilter, trackSelectLocation],
  );

  const onPanoClicked = useCallback(
    (pano: FlyWalkWaypoint) => {
      panoClickedCallback(pano, false);
    },
    [panoClickedCallback],
  );

  const onPanoClickedInMinimap = useCallback(
    (pano: FlyWalkWaypoint) => {
      panoClickedCallback(pano, true);
    },
    [panoClickedCallback],
  );

  return {
    onPanoClicked,
    onPanoClickedInMinimap,
  };
}

function useOnWaypointClicked(
  currentSceneFilter: SceneFilter,
  activeSheet: IElementGenericImgSheet | undefined,
  moveCameraTo: MoveCameraToCbk,
): OnWaypointClicked {
  const dispatch = useAppDispatch();

  const { getState } = useAppStore();

  return useCallback(
    (waypoint: FlyWalkWaypoint) => {
      if (currentSceneFilter === SceneFilter.Pano) {
        dispatch(setActiveElement(waypoint.id));
        dispatch(
          setFlyWalkSceneFilter(
            selectWaypointTargetSceneFilter(waypoint)(getState()),
          ),
        );
      } else {
        const position = selectBestModelCameraFor360(
          waypoint,
          activeSheet,
        )(getState());
        moveCameraTo(position);
      }
    },
    [dispatch, getState, currentSceneFilter, activeSheet, moveCameraTo],
  );
}

/**
 * This component validates that, in walk mode, no more than the allowed number of objects per type
 * are present in the object cache.
 *
 * @returns null
 */
function ObjectCacheValidator(): null {
  useValidateCacheSize(IElementType.pointCloudStream, 2);
  useValidateCacheSize(IElementType.pointCloudStreamWebShare, 2);
  useValidateCacheSize(IElementType.model3dStream, 1);
  useValidateCacheSize(IElementType.img360, 2);
  useValidateCacheSize(IElementType.img2d, 2);
  useValidateCacheSize(IElementType.gaussianSplats, 2);
  return null;
}
